import {
  IPF_MEDIUM_KEY,
  IPF_SOURCE_KEY,
  OWN_DOMAINS,
  TRAFFIC_TYPES,
  UTM_MEDIUM_KEY,
  UTM_SOURCE_KEY,
} from '@workspace/lib/constants/analytics';
import {
  AFFILIATE_CLICK_IDS_REGEX,
  AFFILIATE_MEDIUMS_REGEX,
  PAID_SEARCH_CLICK_IDS_REGEX,
  PAID_SEARCH_MEDIUMS_REGEX,
  PAID_SOCIAL_CLICK_IDS_REGEX,
  PAID_SOCIAL_MEDIUMS_REGEX,
  REFERRAL_MEDIUMS_REGEX,
  SEARCH_ENGINES_REGEX,
  SOCIAL_INDICATORS_REGEX,
  UTM_OR_IPF_REGEX,
} from '@workspace/lib/constants/regex-patterns';
import {
  readEntryTrafficCookie,
  type EntryTraffic,
} from '@workspace/lib/utils/analytics/entry-traffic-cookie';

const normalize = (value: string | null) => {
  if (!value) return '';
  return value.trim().toLowerCase();
};

const hasAnyParamIgnoreCase = (
  searchParams: URLSearchParams,
  pattern: RegExp,
) => {
  for (const key of searchParams.keys()) {
    if (pattern.test(key)) {
      return true;
    }
  }

  return false;
};

const getPrioritizedParam = (
  searchParams: URLSearchParams,
  keysInPriorityOrder: string[],
) => {
  for (const targetKey of keysInPriorityOrder) {
    const lowerTarget = targetKey.toLowerCase();
    for (const [key, value] of searchParams.entries()) {
      if (key.toLowerCase() === lowerTarget && value.trim().length > 0) {
        return value;
      }
    }
  }
  return null;
};

const getReferrerHostname = (referrer: string) => {
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return '';
  }
};

const isOwnHostname = (hostname: string) =>
  OWN_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );

const isPaidSearch = (
  searchParams: URLSearchParams,
  source: string,
  medium: string,
) => {
  const hasPaidSearchClickId = hasAnyParamIgnoreCase(
    searchParams,
    PAID_SEARCH_CLICK_IDS_REGEX,
  );
  const isSearchEngineSource = SEARCH_ENGINES_REGEX.test(source);

  if (
    hasPaidSearchClickId ||
    (isSearchEngineSource && PAID_SEARCH_MEDIUMS_REGEX.test(medium))
  ) {
    return true;
  }

  return false;
};

const isSocialAds = (
  searchParams: URLSearchParams,
  source: string,
  medium: string,
) => {
  const hasPaidSocialClickId = hasAnyParamIgnoreCase(
    searchParams,
    PAID_SOCIAL_CLICK_IDS_REGEX,
  );
  const isSocialSource = SOCIAL_INDICATORS_REGEX.test(source);

  if (
    hasPaidSocialClickId ||
    (isSocialSource && PAID_SOCIAL_MEDIUMS_REGEX.test(medium))
  ) {
    return true;
  }

  return false;
};

const isAffiliate = (searchParams: URLSearchParams, medium: string) => {
  const hasAffiliateClickId = hasAnyParamIgnoreCase(
    searchParams,
    AFFILIATE_CLICK_IDS_REGEX,
  );
  const isAffiliateMedium = AFFILIATE_MEDIUMS_REGEX.test(medium);

  if (hasAffiliateClickId || isAffiliateMedium) {
    return true;
  }

  return false;
};

const isOrganicSearch = (source: string, medium: string) => {
  if (medium === 'organic') return true;
  return isSearchEngineSource(source) && (!medium || medium === 'none');
};

const isReferralSource = (medium: string) => {
  return REFERRAL_MEDIUMS_REGEX.test(medium);
};

const isCrm = (source: string, medium: string) =>
  source === 'crm' && medium === 'email';

const isSearchEngineSource = (source: string) => {
  if (!source) return false;
  return SEARCH_ENGINES_REGEX.test(source);
};

export const hasTrafficAttributionParams = (searchParams: URLSearchParams) => {
  for (const key of searchParams.keys()) {
    if (
      PAID_SEARCH_CLICK_IDS_REGEX.test(key) ||
      PAID_SOCIAL_CLICK_IDS_REGEX.test(key) ||
      AFFILIATE_CLICK_IDS_REGEX.test(key) ||
      UTM_OR_IPF_REGEX.test(key)
    ) {
      return true;
    }
  }
  return false;
};

/**
 * List of traffic types according to the business's spreadsheet.
 * Expand the list with defined parameters if necessary.
 * All non-defined combinations (without params) will be classified as 'Direct'.
 */
export const classifyTrafficType = ({
  searchParams,
  referrer = '',
  currentHostname = '',
  entryTraffic = null,
}: {
  searchParams: URLSearchParams;
  referrer?: string;
  currentHostname?: string;
  entryTraffic?: EntryTraffic | null;
}): string => {
  const hasAttributionParams = hasTrafficAttributionParams(searchParams);

  // Note: no attribution params -> determine traffic type based on referrer hostname
  if (!hasAttributionParams) {
    const referrerHostname = getReferrerHostname(referrer);
    const normalizedCurrentHostName = normalize(currentHostname);

    if (!referrerHostname || referrerHostname === normalizedCurrentHostName) {
      return TRAFFIC_TYPES.direct;
    }

    // Note: arrived from one of our own sites (e.g. the marketing website) ->
    // classify the source/medium it detected from the original referrer
    if (isOwnHostname(referrerHostname)) {
      const source = normalize(entryTraffic?.source ?? null);

      // Note: a hop between our own sites is not a referral
      if (!source || isOwnHostname(source)) {
        return TRAFFIC_TYPES.direct;
      }

      return classifyTrafficType({
        searchParams: new URLSearchParams({
          [UTM_SOURCE_KEY]: source,
          [UTM_MEDIUM_KEY]: entryTraffic?.medium ?? '',
        }),
      });
    }

    if (isSearchEngineSource(referrerHostname)) {
      return TRAFFIC_TYPES.naturalSearch;
    }

    return TRAFFIC_TYPES.referral;
  }

  // Note: attribution params provided -> determine traffic type based on them
  const rawSource = getPrioritizedParam(searchParams, [
    UTM_SOURCE_KEY,
    IPF_SOURCE_KEY,
  ]);
  const rawMedium = getPrioritizedParam(searchParams, [
    UTM_MEDIUM_KEY,
    IPF_MEDIUM_KEY,
  ]);

  const source = normalize(rawSource);
  const medium = normalize(rawMedium);

  if (isPaidSearch(searchParams, source, medium)) {
    return TRAFFIC_TYPES.paidSearch;
  }

  if (isSocialAds(searchParams, source, medium)) {
    return TRAFFIC_TYPES.socialAds;
  }

  if (isAffiliate(searchParams, medium)) {
    return TRAFFIC_TYPES.affiliate;
  }

  if (isCrm(source, medium)) {
    return TRAFFIC_TYPES.crm;
  }

  if (isOrganicSearch(source, medium)) {
    return TRAFFIC_TYPES.naturalSearch;
  }

  if (isReferralSource(medium)) {
    return TRAFFIC_TYPES.referral;
  }

  return TRAFFIC_TYPES.direct;
};

/**
 * Convenience wrapper that detects traffic type from the current browser context.
 * Browser-only — accesses `window.location`, `document.referrer` and
 * `document.cookie` at call time.
 * Must not be called during SSR/Node execution; use `classifyTrafficType` with
 * explicit params for server-safe contexts.
 */
export const detectTrafficType = (
  searchParams?: URLSearchParams,
  referrer?: string,
) => {
  if (typeof window === 'undefined') {
    return TRAFFIC_TYPES.direct;
  }

  const params = searchParams ?? new URLSearchParams(window.location.search);

  return classifyTrafficType({
    searchParams: params,
    referrer: referrer ?? document.referrer,
    currentHostname: window.location.hostname,
    entryTraffic: readEntryTrafficCookie(),
  });
};
